import { AtticErrorCode } from "@dashboard/graphql";
import commonErrorMessages, {
  getCommonFormFieldErrorMessage,
} from "@dashboard/utils/errors/common";
import { defineMessages, type IntlShape, type MessageDescriptor } from "react-intl";

/**
 * Merchant wording for every Digital Assets member of AtticErrorCode. The
 * backend's `message` is an operator's sentence ("img/hero.png already exists;
 * delete it or use another name"), so the screens lead with these and keep the
 * backend's sentence only as detail, where it names the specific file, folder
 * or cause.
 */
export const atticAssetErrorMessages = defineMessages({
  directoryReserved: {
    id: "9YK/xF",
    defaultMessage: "This folder name is reserved for the store's own files. Choose another.",
    description: "digital assets error, ASSET_DIRECTORY_RESERVED",
  },
  directoryInUse: {
    id: "ZDT2eU",
    defaultMessage: "This directory cannot be deleted while it holds files.",
    description: "digital assets error, ASSET_DIRECTORY_IN_USE",
  },
  directoryReadOnly: {
    id: "BZJh94",
    defaultMessage: "An app manages this directory, so it is read-only here.",
    description: "digital assets error, ASSET_DIRECTORY_READ_ONLY",
  },
  directoryKeyInvalid: {
    id: "Io4Bau",
    defaultMessage:
      "A directory needs a name, and its folder may use only lowercase letters, digits and hyphens.",
    description: "digital assets directory dialog error, ASSET_KEY_INVALID",
  },
  directoryKeyTaken: {
    id: "f91WqC",
    defaultMessage: "A directory with this folder already exists.",
    description: "digital assets directory dialog error, ASSET_KEY_TAKEN",
  },
  fileKeyInvalid: {
    id: "FRN8f1",
    defaultMessage: "This file or folder name cannot be used.",
    description: "digital assets upload error, ASSET_KEY_INVALID",
  },
  fileKeyTaken: {
    id: "YNrHJC",
    defaultMessage: "This name is already taken in the directory. Rename the file to upload it.",
    description:
      "digital assets upload error, ASSET_KEY_TAKEN: the same name, or an image that shares its resized URLs (same name, other extension)",
  },
  uploadRefused: {
    id: "ic9fph",
    defaultMessage: "The file was refused.",
    description: "digital assets upload error, ASSET_UPLOAD_REFUSED",
  },
  uploadInProgress: {
    id: "k2Ti5H",
    defaultMessage:
      "This name is held by an upload that has not finished. Try again in a few minutes, or rename the file.",
    description: "digital assets upload error, ASSET_UPLOAD_IN_PROGRESS",
  },
  uploadNotLanded: {
    id: "IPEOER",
    defaultMessage: "The file has not arrived in storage yet. Try again in a moment.",
    description: "digital assets upload error, ASSET_UPLOAD_NOT_LANDED",
  },
  fileGone: {
    id: "oRZYyp",
    defaultMessage: "This file no longer exists; it was already deleted.",
    description: "digital assets delete error: the file was already gone (NOT_FOUND)",
  },
  directoryGone: {
    id: "3QUu2/",
    defaultMessage: "This directory no longer exists; it was already deleted.",
    description: "digital assets delete error: the directory was already gone (NOT_FOUND)",
  },
  alreadyConfirmed: {
    id: "ryNm0u",
    defaultMessage: "This file is already live.",
    description: "digital assets upload error, ASSET_ALREADY_CONFIRMED",
  },
  quotaExceeded: {
    id: "XfGiEn",
    defaultMessage: "The store's storage limit is reached. Delete files to make room.",
    description: "digital assets error, ASSET_QUOTA_EXCEEDED",
  },
  storageUnavailable: {
    id: "bQdy6/",
    defaultMessage: "Storage is not available right now. Try again in a moment.",
    description: "digital assets error, ASSET_STORAGE_UNAVAILABLE",
  },
  withDetail: {
    id: "5AHFIh",
    defaultMessage: "{message} ({detail})",
    description: "digital assets error: merchant sentence, then the backend's specific detail",
  },
});

export type AtticAssetErrorContext = "directory" | "file";

interface CodeMessage {
  message: MessageDescriptor;
  /** The backend's sentence names something specific (a key, a folder, a cause). */
  detail: boolean;
}

const sharedCodes: Partial<Record<AtticErrorCode, CodeMessage>> = {
  [AtticErrorCode.ASSET_DIRECTORY_RESERVED]: {
    message: atticAssetErrorMessages.directoryReserved,
    detail: true,
  },
  [AtticErrorCode.ASSET_DIRECTORY_IN_USE]: {
    message: atticAssetErrorMessages.directoryInUse,
    detail: true,
  },
  [AtticErrorCode.ASSET_DIRECTORY_READ_ONLY]: {
    message: atticAssetErrorMessages.directoryReadOnly,
    detail: false,
  },
  [AtticErrorCode.ASSET_UPLOAD_REFUSED]: {
    message: atticAssetErrorMessages.uploadRefused,
    detail: true,
  },
  [AtticErrorCode.ASSET_UPLOAD_IN_PROGRESS]: {
    message: atticAssetErrorMessages.uploadInProgress,
    detail: false,
  },
  [AtticErrorCode.ASSET_ALREADY_CONFIRMED]: {
    message: atticAssetErrorMessages.alreadyConfirmed,
    detail: false,
  },
  [AtticErrorCode.ASSET_QUOTA_EXCEEDED]: {
    message: atticAssetErrorMessages.quotaExceeded,
    detail: false,
  },
  [AtticErrorCode.ASSET_UPLOAD_NOT_LANDED]: {
    message: atticAssetErrorMessages.uploadNotLanded,
    detail: false,
  },
  // The backend's sentence is either this one again or an operator's cause
  // (no environment, no region): neither tells the merchant more.
  [AtticErrorCode.ASSET_STORAGE_UNAVAILABLE]: {
    message: atticAssetErrorMessages.storageUnavailable,
    detail: false,
  },
};

const codesByContext: Record<
  AtticAssetErrorContext,
  Partial<Record<AtticErrorCode, CodeMessage>>
> = {
  directory: {
    ...sharedCodes,
    [AtticErrorCode.ASSET_KEY_INVALID]: {
      message: atticAssetErrorMessages.directoryKeyInvalid,
      detail: true,
    },
    [AtticErrorCode.ASSET_KEY_TAKEN]: {
      message: atticAssetErrorMessages.directoryKeyTaken,
      detail: true,
    },
  },
  file: {
    ...sharedCodes,
    [AtticErrorCode.ASSET_KEY_INVALID]: {
      message: atticAssetErrorMessages.fileKeyInvalid,
      detail: true,
    },
    [AtticErrorCode.ASSET_KEY_TAKEN]: {
      message: atticAssetErrorMessages.fileKeyTaken,
      detail: true,
    },
  },
};

export interface AtticAssetErrorLike {
  code?: AtticErrorCode | null;
  field?: string | null;
  message?: string | null;
}

/**
 * The sentence a merchant sees for one refusal: this module's wording for an
 * ASSET_* code (with the backend's sentence as detail where it names the
 * specific thing), else the backend's sentence, else the Dashboard's common
 * wording for the code, else a generic one. Never empty, even for an error
 * that carries no message.
 */
export const getAtticAssetErrorMessage = (
  error: AtticAssetErrorLike,
  intl: IntlShape,
  context: AtticAssetErrorContext,
): string => {
  const mapped = error.code ? codesByContext[context][error.code] : undefined;
  const detail = error.message?.trim();

  if (mapped) {
    const message = intl.formatMessage(mapped.message);

    return mapped.detail && detail
      ? intl.formatMessage(atticAssetErrorMessages.withDetail, { message, detail })
      : message;
  }

  if (detail) {
    return detail;
  }

  const common = error.code
    ? getCommonFormFieldErrorMessage({ code: error.code, field: error.field ?? null }, intl)
    : undefined;

  return common ?? intl.formatMessage(commonErrorMessages.unknownError);
};

/**
 * A delete refused because the thing is already gone: NOT_FOUND, or a
 * GRAPHQL_ERROR on the id argument (an id the API can no longer resolve).
 * The screen closes its dialog and refetches, so the stale row disappears.
 */
export const isAtticAssetGoneRefusal = (error: AtticAssetErrorLike): boolean =>
  error.code === AtticErrorCode.NOT_FOUND ||
  (error.code === AtticErrorCode.GRAPHQL_ERROR && error.field === "id");

/** Merchant wording for a refused delete: "already gone" for {@link isAtticAssetGoneRefusal}. */
export const getAtticAssetDeleteRefusalMessage = (
  error: AtticAssetErrorLike,
  intl: IntlShape,
  context: AtticAssetErrorContext,
): string =>
  isAtticAssetGoneRefusal(error)
    ? intl.formatMessage(
        context === "file"
          ? atticAssetErrorMessages.fileGone
          : atticAssetErrorMessages.directoryGone,
      )
    : getAtticAssetErrorMessage(error, intl, context);
